import { Camera, Flashlight, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * Заснемане на документ. Първо се опитва жив образ с рамка-водач — така
 * листът излиза цял и прав, а не отрязан наполовина, както става с бърза
 * снимка от системната камера. Ако браузърът не дава достъп до камерата
 * (стар iOS, отказано разрешение), се минава през системната камера.
 */
export function CameraCapture({
  title,
  hint,
  onCapture,
  onClose,
}: {
  title: string;
  hint: string;
  onCapture: (file: Blob) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setFailed(true);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1440 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch {
        setFailed(true);
      }
    }
    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({
        advanced: [{ torch: !torchOn } as unknown as MediaTrackConstraintSet],
      });
      setTorchOn((value) => !value);
    } catch {
      // Фенерчето не се управлява на всички телефони — тихо се пропуска.
    }
  }

  function shoot() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => blob && onCapture(blob), 'image/jpeg', 0.92);
  }

  if (failed) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/80 p-4">
        <div className="rounded-2xl bg-paper p-4">
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="mt-2 text-lg text-muted">{hint}</p>
          <label className="tap mt-4 flex items-center justify-center gap-3 rounded-2xl bg-go px-4 py-5 text-2xl font-bold text-white">
            <Camera size={30} />
            Отвори камерата
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onCapture(file);
              }}
            />
          </label>
          <button
            type="button"
            onClick={onClose}
            className="tap mt-2 w-full rounded-2xl border-2 border-line px-4 py-4 text-xl font-bold"
          >
            Затвори
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="safe-top flex items-center justify-between px-3 py-2 text-white">
        <span className="text-xl font-bold">{title}</span>
        <button type="button" onClick={onClose} aria-label="Затвори" className="tap px-3">
          <X size={34} />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          className="h-full w-full object-cover"
          aria-label="Изображение от камерата"
        />
        {/* Рамка-водач: листът трябва да опира до ъглите. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-5">
          <div className="relative aspect-[210/297] w-full max-w-[92%] rounded-lg border-4 border-white/80">
            <Corner className="left-[-4px] top-[-4px] border-l-8 border-t-8" />
            <Corner className="right-[-4px] top-[-4px] border-r-8 border-t-8" />
            <Corner className="bottom-[-4px] left-[-4px] border-b-8 border-l-8" />
            <Corner className="bottom-[-4px] right-[-4px] border-b-8 border-r-8" />
          </div>
        </div>
        <p className="absolute inset-x-0 bottom-3 px-4 text-center text-lg font-semibold text-white drop-shadow">
          {hint}
        </p>
      </div>

      <div className="safe-bottom flex items-center justify-between gap-4 bg-black px-6 py-4">
        <button
          type="button"
          onClick={toggleTorch}
          aria-label="Фенерче"
          className="tap flex w-20 items-center justify-center rounded-2xl border-2 border-white/40 text-white"
        >
          <Flashlight size={30} fill={torchOn ? 'currentColor' : 'none'} />
        </button>
        <button
          type="button"
          onClick={shoot}
          disabled={!ready}
          aria-label="Снимай"
          className="h-24 w-24 rounded-full border-8 border-white bg-white/20 disabled:opacity-40"
        />
        <span className="w-20" />
      </div>
    </div>
  );
}

function Corner({ className }: { className: string }) {
  return <span className={`absolute h-10 w-10 border-[color:#f5a524] ${className}`} />;
}
